import React, { useState, useRef, useEffect } from "react";
import { FaTrash } from "react-icons/fa";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from "@syncfusion/ej2-react-grids";
import EmptyData from "../components/EmptyData";
import Spinner from "../Pages/Components/Spinner";
import { paginationService } from "../Services/pagination.service";
import DeleteModal from "../Pages/Components/Modals/Delete-modal";

const ROW_HEIGHT          = 30;
const HEADER_PAGER_BUDGET = 100;
const BOTTOM_MARGIN       = 30;
const MIN_PAGE            = 5;
const MAX_PAGE            = 100;

const formatDate = (raw) => {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return raw; }
};

const formatCurrency = (val) =>
  Number(val).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatQty = (val) =>
  Number(val).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const FuelGrid = ({
  refuelings = [],
  isLoading  = false,
  isDeleting = false,
  onDelete,
}) => {
  const [deleteTarget, setDeleteTarget] = useState(null); // item a deletar
  const [pageSize, setPageSize]         = useState(10);
  const gridAnchorRef                   = useRef(null);

  useEffect(() => {
    if (!isLoading)
      paginationService.calculateGridMetrics(
        gridAnchorRef, BOTTOM_MARGIN, HEADER_PAGER_BUDGET,
        ROW_HEIGHT, MIN_PAGE, MAX_PAGE,
        refuelings, window.innerHeight, setPageSize
      );

    let t = null;
    const onResize = () => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(
        () => paginationService.calculateGridMetrics(
          gridAnchorRef, BOTTOM_MARGIN, HEADER_PAGER_BUDGET,
          ROW_HEIGHT, MIN_PAGE, MAX_PAGE,
          refuelings, window.innerHeight, setPageSize
        ), 50
      );
    };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); if (t) window.clearTimeout(t); };
  }, [isLoading, refuelings]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.refuelingId ?? deleteTarget.id;
    setDeleteTarget(null);
    await onDelete({ refuelingId: id });
  };

  // ── Templates ──
  const dateTemplate  = (props) => formatDate(props.refuelingDate ?? props.dateTime ?? props.date);
  const fuelTemplate  = (props) => props.fuelTypeName ?? props.fuelType ?? props.combustivel ?? "—";
  const qtyTemplate   = (props) => formatQty(props.quantity ?? props.quantidade ?? 0);
  const valueTemplate = (props) => formatCurrency(props.amount ?? props.value ?? props.valor ?? 0);

  const actionsTemplate = (props) => (
    <FaTrash
      style={{ cursor: "pointer", color: "var(--text-primary)", fontSize: 16 }}
      title="Deletar abastecimento"
      onClick={() => setDeleteTarget(props)}
    />
  );

  if (isLoading) return <Spinner />;
  if (!refuelings.length) return <EmptyData />;

  return (
    <div className="grid-section">
      <div ref={gridAnchorRef} />

      <DeleteModal
        show={!!deleteTarget}
        handleClose={() => setDeleteTarget(null)}
        itemName={deleteTarget ? `abastecimento de ${formatDate(deleteTarget.refuelingDate)}` : ""}
        itemRemoveId={deleteTarget?.refuelingId ?? deleteTarget?.id}
        callBackRemove={handleDeleteConfirm}
      />

      <GridComponent
        dataSource={refuelings}
        allowPaging={true}
        pageSettings={{ pageSize }}
        rowHeight={ROW_HEIGHT}
        height="auto"
      >
        <ColumnsDirective>
          <ColumnDirective headerText="Data/Hora"   width="160" template={dateTemplate} />
          <ColumnDirective headerText="Combustível" width="140" template={fuelTemplate} />
          <ColumnDirective headerText="Qtd (l/m³)"  width="120" textAlign="Right" template={qtyTemplate} />
          <ColumnDirective headerText="Valor"        width="130" textAlign="Right" template={valueTemplate} />
          <ColumnDirective headerText="Ações"        width="80"  template={actionsTemplate} />
        </ColumnsDirective>
        <Inject services={[Page]} />
      </GridComponent>
    </div>
  );
};

export default FuelGrid;
